import { zodResolver } from '@hookform/resolvers/zod';
import { getNumber } from '@util/createListingHelpers';
import { createBid } from '@util/firestore/bid/bid.service';
import { updateOffer } from '@util/firestore/offers';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';
import { formatCurrency } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { BidDocument } from 'models/bid';
import { ProductDocument } from 'models/product';
import { useForm } from 'react-hook-form';
import { updateBidFormSchema } from '../types';
import { UpdateBidFormSchemaType } from '../types';
import { getProductById } from '@util/firestore/products';

export const useUpdateBid = ({
  product,
  updateBidPrice,
  onSubmitSuccess,
}: {
  product: ProductDocument;
  updateBidPrice: number;
  onSubmitSuccess: () => void;
}) => {
  const useFormReturn = useForm<UpdateBidFormSchemaType>({
    resolver: zodResolver(updateBidFormSchema),
    defaultValues: {
      price: updateBidPrice,
    },
  });
  const { userDoc } = useAuth();
  const { highestBid, usersBids } = useRealTimeBids(product.id, userDoc?.uid);
  const { showErrorToast, showSuccessToast } = useToastContext();
  const onSubmit = async () => {
    if (!userDoc) return;
    if (!usersBids.length) {
      // shouldn't happen
      showErrorToast('You must place a bid before updating it!');
      return;
    }
    if (highestBid && updateBidPrice < highestBid.bid_price + 1) {
      showErrorToast(
        `Your bid must be at least ${formatCurrency(
          1
        )} higher than the current bid!`
      );
      return;
    }

    const maxBid = getNumber(useFormReturn.getValues('maxPrice'));
    if (maxBid && maxBid < updateBidPrice + 1) {
      showErrorToast(
        `Your max bid must be at least ${formatCurrency(
          1
        )} higher than your bid price!`
      );
      return;
    }
    const now = Date.now();
    const currProduct = await getProductById(product.id);
    if (
      !currProduct ||
      currProduct.end_time! < now ||
      currProduct.out_of_stock
    ) {
      showErrorToast('This auction has ended!');
      return;
    }
    const bid: BidDocument = {
      bid_price: getNumber(updateBidPrice),
      created: Date.now(),
      auto: false,
      offer_id: usersBids[0].offer_id,
      product_id: product.id,
      uid: userDoc.uid,
      username:
        userDoc.username.charAt(0) +
        '***' +
        userDoc.username.charAt(userDoc.username.length - 1),
    };
    if (maxBid) {
      bid.max_bid = maxBid;
    }
    await createBid(bid);
    showSuccessToast('Bid updated successfully');
    onSubmitSuccess();
  };

  return { useFormReturn, onSubmit };
};
